import React, { useState, useCallback } from 'react';
import { stripeConnectApi, inviteApi } from '../../api';
import { ImportResult } from '../../api_types';
import { useAuth } from '../../contexts/AuthContext';
import CSVUploader from '../../shared/components/CSVUploader';

interface CSVRow {
    firstName: string;
    lastName: string;
    email: string;
    startDate?: string;
}

interface ExtendedImportResult extends ImportResult {
    inviteStatus?: 'sent' | 'failed' | 'skipped';
    inviteError?: string;
}

const ImportSubscribers: React.FC<{ businessId: string, facilityId: string }> = ({ businessId, facilityId }) => {
    const { user } = useAuth();
    const [subscribers, setSubscribers] = useState<CSVRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<ExtendedImportResult[] | null>(null);
    const [sendInvites, setSendInvites] = useState(true);
    const [inviteProgress, setInviteProgress] = useState<{ current: number; total: number } | null>(null);

    const handleFileLoaded = useCallback((data: any[]) => {
        // Process and validate CSV data - only basic fields
        const processedSubscribers: CSVRow[] = data
            .filter(row => row.firstName && row.lastName && row.email) // Filter out invalid rows
            .map(row => ({
                firstName: String(row.firstName || '').trim(),
                lastName: String(row.lastName || '').trim(),
                email: String(row.email || '').trim().toLowerCase(),
                startDate: row.startDate || new Date().toISOString().split('T')[0]
            }));

        // Validate emails
        const validSubscribers = processedSubscribers.filter(subscriber => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(subscriber.email);
        });

        if (validSubscribers.length !== processedSubscribers.length) {
            setError(`${processedSubscribers.length - validSubscribers.length} rows removed due to invalid email addresses`);
        }

        setSubscribers(validSubscribers);
        setError(null);
    }, []);

    const handleError = useCallback((error: Error) => {
        setError(error.message);
    }, []);

    const sendInvitesToImportedMembers = async (successfulImports: ExtendedImportResult[]): Promise<ExtendedImportResult[]> => {
        if (!sendInvites || !user?.facilityId) {
            return successfulImports.map(result => ({ ...result, inviteStatus: 'skipped' }));
        }

        try {
            setInviteProgress({ current: 0, total: 1 });
            
            // Send setup emails to all migrating users
            const data = await inviteApi.sendMigrationSetupEmails(businessId, facilityId);

            setInviteProgress({ current: 1, total: 1 });

            // Update all successful imports with invite status
            return successfulImports.map(result => ({
                ...result,
                inviteStatus: result.status === 'success' ? 'sent' : 'skipped'
            }));

        } catch (error) {
            console.error('Error sending setup emails:', error);
            setError(`Failed to send setup emails: ${error instanceof Error ? error.message : 'Unknown error'}`);
            
            // Mark all as failed
            return successfulImports.map(result => ({
                ...result,
                inviteStatus: 'failed'
            }));
        } finally {
            setInviteProgress(null);
        }
    };

    const handleImport = async () => {
        if (!facilityId || !businessId) {
            setError('No facility ID or business ID found');
            return;
        }

        if (subscribers.length === 0) {
            setError('No valid subscribers to import');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('Starting import with data:', {
                facilityId: facilityId,
                businessId: businessId,
                subscribersCount: subscribers.length,
                sendInvites
            });

            // Step 1: Import subscribers (creates pending users only)
            const response = await stripeConnectApi.importSubscriptions(
                businessId,
                facilityId,
                '', // No plan needed since we don't create subscriptions during import
                subscribers.map(sub => ({
                    ...sub,
                    startDate: sub.startDate || new Date().toISOString().split('T')[0] // Ensure startDate is provided
                }))
            );
            
            let finalResults: ExtendedImportResult[] = response.results;

            // Step 2: Send setup emails to successful imports if enabled
            if (sendInvites) {
                finalResults = await sendInvitesToImportedMembers(finalResults);
            }

            setResults(finalResults);
            setSubscribers([]);
            setError(null);
        } catch (err) {
            console.error('Import error:', err);
            setError(err instanceof Error ? err.message : 'Failed to import subscribers');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount / 100);
    };

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Import Subscribers</h2>
            <p className="text-gray-600 mb-6">
                Upload a CSV file with subscriber information to create subscriptions.
            </p>
            
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded border border-red-300">
                    {error}
                </div>
            )}

            <div className="mb-6">
                <label className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        checked={sendInvites}
                        onChange={(e) => setSendInvites(e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                        Send account setup emails after import (Recommended)
                    </span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                    Members will receive an email to set their password and complete account setup.
                </p>
            </div>

            <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                    Upload Subscriber CSV File
                </label>
                <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">Required CSV Columns:</h4>
                    <div className="text-sm text-blue-700">
                        <div className="grid grid-cols-2 gap-2">
                            <div><strong>firstName</strong> - First name</div>
                            <div><strong>lastName</strong> - Last name</div>
                            <div><strong>email</strong> - Email address</div>
                            <div><strong>startDate</strong> - Start date (optional)</div>
                        </div>
                    </div>
                </div>
                <CSVUploader
                    onFileLoaded={handleFileLoaded}
                    onError={handleError}
                    accept=".csv"
                />
            </div>
            
            {subscribers.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">
                        Preview ({subscribers.length} subscribers ready for import)
                    </h3>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Start Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {subscribers.slice(0, 10).map((subscriber, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                                            {subscriber.firstName} {subscriber.lastName}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                                            {subscriber.email}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                                            {subscriber.startDate}
                                        </td>
                                    </tr>
                                ))}
                                {subscribers.length > 10 && (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-2 text-center text-gray-500 text-sm">
                                            ... and {subscribers.length - 10} more subscribers
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    <button
                        onClick={handleImport}
                        disabled={loading}
                        className={`mt-4 w-full py-3 px-4 rounded-md text-white font-medium ${
                            loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                {inviteProgress ? (
                                    `Sending invites... (${inviteProgress.current}/${inviteProgress.total})`
                                ) : (
                                    'Importing Subscribers...'
                                )}
                            </div>
                        ) : (
                            `Import ${subscribers.length} Subscribers${sendInvites ? ' & Send Invites' : ''}`
                        )}
                    </button>
                </div>
            )}
            
            {inviteProgress && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-700">
                            Sending setup emails... ({inviteProgress.current}/{inviteProgress.total})
                        </span>
                    </div>
                </div>
            )}

            {results && results.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Import Results</h3>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Setup Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {results.map((result, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {result.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                result.status === 'success' 
                                                    ? 'bg-green-100 text-green-800'
                                                    : result.status === 'skipped'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {result.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {result.inviteStatus && (
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    result.inviteStatus === 'sent' 
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : result.inviteStatus === 'failed'
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {result.inviteStatus}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {result.status === 'success' 
                                                ? 'Account created successfully'
                                                : result.status === 'skipped'
                                                ? result.message || 'User already exists'
                                                : result.error}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportSubscribers; 